import React, { useState, useEffect } from 'react';
import API from '../api';
import { IconRefresh, IconCheck, IconAlert, IconInsights } from '../components/Icons';

export default function Insights() {
  const [data, setData]       = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchInsights = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get('/api/analytics/insights' + (force ? '?force=1' : ''));
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  // Fetch live channel stats separately on every load
  const fetchLiveStats = async () => {
    try {
      const res = await API.get('/api/analytics/channel');
      setLiveStats(res.data);
    } catch {}
  };

  useEffect(() => {
    fetchInsights();
    fetchLiveStats();
  }, []);

  return (
    <div>
      <div className="page-header" style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between'}}>
        <div>
          <div className="page-title">Insights</div>
          <div className="page-subtitle">AI analysis of your YouTube performance</div>
        </div>
        <button className="btn btn-ghost" onClick={() => fetchInsights()} disabled={loading} style={{display:'flex', alignItems:'center', gap:6}}>
          <IconRefresh /> {loading ? 'Analysing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-msg" style={{marginBottom:24}}>
          {error === 'YouTube not connected'
            ? 'Connect your YouTube channel on the Dashboard to see insights.'
            : error}
        </div>
      )}

      {loading && (
        <div className="empty-state">
          <div className="empty-icon"><IconInsights width={48} height={48} /></div>
          <div className="empty-title">Claude is analysing your channel...</div>
          <div className="empty-desc">This takes about 10-15 seconds</div>
        </div>
      )}

      {data && !loading && (
        <>
          {/* Summary — uses live stats for numbers */}
          <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28, marginBottom:24}}>
            <div className="section-title">Overview</div>
            <p style={{color:'var(--text-dim)', lineHeight:1.7, fontSize:15, marginBottom:20}}>{data.insights.summary}</p>
            <div style={{display:'flex', gap:32}}>
              <div>
                <div style={{fontSize:11, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:4}}>Total Views (90d)</div>
                <div style={{fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'var(--accent)'}}>
                  {liveStats ? (liveStats.period28?.views || 0).toLocaleString() : (data.analyticsData.totalViews || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{fontSize:11, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:4}}>Total Likes (90d)</div>
                <div style={{fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'var(--accent)'}}>
                  {liveStats ? (liveStats.period28?.likes || 0).toLocaleString() : (data.analyticsData.totalLikes || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{fontSize:11, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:4}}>Recommended Posts/Day</div>
                <div style={{fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'var(--accent)'}}>{data.insights.recommendedFrequency?.perDay}</div>
              </div>
            </div>
          </div>

          {/* Best times */}
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24}}>
            <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:24}}>
              <div className="section-title">Best Days to Post</div>
              <div style={{display:'flex', flexDirection:'column', gap:10}}>
                {data.insights.bestDays?.map((d, i) => (
                  <div key={i} style={{display:'flex', flexDirection:'column', gap:4, paddingBottom:10, borderBottom:'1px solid var(--border)'}}>
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                      <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:15}}>{d.day}</div>
                      {d.avgViews > 0 && (
                        <div style={{fontFamily:'var(--font-display)', fontWeight:700, color:'var(--accent)', fontSize:13}}>
                          {d.avgViews.toLocaleString()} avg views
                        </div>
                      )}
                    </div>
                    <div style={{fontSize:12, color:'var(--text-muted)', lineHeight:1.5}}>{d.reason}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:24}}>
              <div className="section-title">Best Times to Post</div>
              <div style={{display:'flex', flexDirection:'column', gap:10}}>
                {data.insights.bestHours?.map((h, i) => (
                  <div key={i} style={{display:'flex', flexDirection:'column', gap:4, paddingBottom:10, borderBottom:'1px solid var(--border)'}}>
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                      <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:15}}>{h.hour}</div>
                    </div>
                    <div style={{fontSize:12, color:'var(--text-muted)', lineHeight:1.5}}>{h.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Weekly schedule */}
          <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:24, marginBottom:24}}>
            <div className="section-title">Recommended Weekly Schedule</div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8}}>
              {data.insights.weeklySchedule?.map((day, i) => (
                <div key={i} style={{background:'var(--surface2)', borderRadius:8, padding:12, textAlign:'center'}}>
                  <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-muted)', marginBottom:8}}>
                    {day.day.slice(0,3)}
                  </div>
                  {day.times.map((t, j) => (
                    <div key={j} style={{background:'var(--accent-dim)', color:'var(--accent)', fontSize:12, fontWeight:600, padding:'4px 6px', borderRadius:4, marginBottom:4}}>
                      {t}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div style={{fontSize:12, color:'var(--text-muted)', marginTop:12}}>
              {data.insights.recommendedFrequency?.reasoning}
            </div>
          </div>

          {/* Insights */}
          {data.insights.insights?.length > 0 && (
            <div style={{marginBottom:24}}>
              <div className="section-title">Insights</div>
              <div style={{display:'flex', flexDirection:'column', gap:12}}>
                {data.insights.insights.slice(0,4).map((insight, i) => (
                  <div key={i} style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:20}}>
                    <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
                      <IconCheck />
                      <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:15}}>{insight.title}</div>
                    </div>
                    <div style={{color:'var(--text-dim)', fontSize:13, lineHeight:1.6, marginBottom:10}}>{insight.description}</div>
                    <div style={{background:'var(--accent-dim)', color:'var(--accent)', fontSize:12, fontWeight:600, padding:'6px 10px', borderRadius:6, display:'inline-block'}}>
                      → {insight.action}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Things to watch */}
          {data.insights.thingsToWatch?.length > 0 && data.insights.thingsToWatch?.some(w => w.title) && (
            <div>
              <div className="section-title">Things to watch</div>
              <div style={{display:'flex', flexDirection:'column', gap:12}}>
                {data.insights.thingsToWatch.map((w, i) => (
                  <div key={i} style={{background:'rgba(255,165,0,0.05)', border:'1px solid rgba(255,165,0,0.2)', borderRadius:12, padding:20}}>
                    <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
                      <IconAlert />
                      <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:15, color:'orange'}}>{w.title}</div>
                    </div>
                    <div style={{color:'var(--text-dim)', fontSize:13, lineHeight:1.6}}>{w.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
